/* ============================================================================
   HOW MANY PEOPLE A ROOM SLEEPS, AND HOW MANY ARE IN IT. Sprint 49.

   The client's answer on 17 September: *"in all location it is 1 room with 4 person or
   1 room with 2 person"*. Both hotels, every building.

   **THIS IS THE CLIENT HALF OF `Housekeeping.Domain.Rooms.RoomCapacity`** and it is
   deliberately the same two functions with the same two names. Three screens draw this —
   the room detail on the handset, the room detail on the web, and the administration
   screen — and a screen that decided for itself what "over capacity" meant would be a
   fourth opinion about a number the server already has an answer for.
   ========================================================================= */

/**
 * How many people the property system says are in the room, or **null when it has not
 * said** — which is not zero, and the difference matters on every screen that draws it.
 *
 * The PMS is entitled to send a stay with no head count on it. "0 people" printed against
 * a room a guest is standing in is a lie the product would be telling in its own voice; a
 * VACANT room is a separate question, answered by the occupancy state rather than by this.
 */
export function peopleInRoom(
  adults: number | null | undefined,
  children: number | null | undefined,
): number | null {
  if ((adults ?? null) === null && (children ?? null) === null) return null
  return (adults ?? 0) + (children ?? 0)
}

/**
 * More people than beds — a family of three in a room for two, with a mattress on the
 * floor. Ordinary in a pilgrimage hotel.
 *
 * **NOT AN ERROR, AND IT MUST NOT BE DRAWN AS ONE.** The hotel has sold the room and the
 * guests are in it. What it changes is what an attendant carries down the corridor: a
 * room for four cleaned for four with five people in it is one towel short every day, and
 * nobody ever finds out why the guest keeps asking.
 *
 * **FALSE WHEN NOBODY HAS COUNTED.** Guessing that an unknown head count is an over-full
 * room would put a note on half the board, and a note on half the board is read by nobody.
 */
export function isOverCapacity(
  capacity: number,
  adults: number | null | undefined,
  children: number | null | undefined,
): boolean {
  const people = peopleInRoom(adults, children)
  return people !== null && people > capacity
}
